import { useState } from 'react';
import type { BillingAllowance } from '@/state/billing/useBillingAllowance';
import type { BillingUsage } from '@/state/billing/useBillingUsage';
import type { BillingSettingsManager } from '@/state/billing/useBillingSettings';
import { formatBillingDate, formatUnits } from '@/state/billing/billing-format';
import { BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { MetricGrid, MetricTile, Panel } from '../../ConsolePanel';
import { KeepPlanDialog } from './KeepPlanDialog';
import { SectionStatusLine } from './SectionStatusLine';

type UsageSummaryProps = { manager: BillingSettingsManager };

/**
 * The plan's website sessions, the billed unit, for the period the plan counts them in.
 * The plans are flat, so there is no accrued or projected cost to show: the
 * allowance tile says how much of the plan is used and when it resets. That window
 * runs from the subscription's start, not the calendar, so an account with a plan
 * shows that one count and nothing else: a second total by the calendar month
 * would be the same traffic cut differently, and a customer could not tell which
 * one billing follows. An account with no plan window (invoice, free) has only the
 * calendar-month count to show.
 */
export const UsageSummary = ({ manager }: UsageSummaryProps) => {
  const { usage: usageSection, billedBy, allowance: allowanceSection, canManageBilling } = manager;
  const allowance = allowanceSection.allowance;
  const { usage } = usageSection;

  const byPlan = !!allowance;
  const measure = byPlan ? "Sessions in your plan's current period, updated hourly." : 'Month-to-date, updated hourly.';

  return (
    <Panel
      tourAnchor="billing-usage"
      title={byPlan ? 'Usage this period' : 'Usage this month'}
      description={billedBy ? `Billed through ${billedBy.name}. ${measure}` : measure}
    >
      {!usage ? (
        <SectionStatusLine
          status={usageSection}
          loadingText="Loading usage..."
          errorText="Usage could not be loaded."
        />
      ) : (
        <>
          {allowance?.held && <HeldNotice allowance={allowance} />}
          {allowance?.plannedUpgrade && (
            <PlannedUpgradeNotice
              allowance={allowance}
              canManage={canManageBilling}
              isKeeping={allowanceSection.isKeepingPlan}
              onKeep={allowanceSection.keepPlan}
            />
          )}
          <UsageTiles usage={usage} allowance={allowance} allowanceLoading={allowanceSection.isLoading} />
        </>
      )}
    </Panel>
  );
};

type UsageTilesProps = { usage: BillingUsage; allowance: BillingAllowance | null; allowanceLoading: boolean };

const UsageTiles = ({ usage, allowance, allowanceLoading }: UsageTilesProps) => (
  <MetricGrid>
    {allowance && <AllowanceTile allowance={allowance} />}
    {!allowance && !allowanceLoading && (
      <MetricTile
        label="Website sessions"
        value={formatUnits(usage.sessions)}
        description="Counted once per visitor tab per website"
        subtitle="This calendar month"
      />
    )}
  </MetricGrid>
);

type AllowanceTileProps = { allowance: BillingAllowance };

/**
 * The plan's sessions: used so far in the current window against what the
 * plan includes. Past the allowance the bar stays full and the figure keeps
 * counting, since serving never stops; the account is moved up a plan instead.
 */
const AllowanceTile = ({ allowance }: AllowanceTileProps) => {
  const { included, used, windowEnd, projected } = allowance;
  const percent = included ? Math.min(100, Math.round((used / included) * 100)) : 0;
  const projectedPercent = included && projected !== null ? Math.round((projected / included) * 100) : null;

  return (
    <MetricTile
      label="Sessions this period"
      value={included ? `${formatUnits(used)} of ${formatUnits(included)}` : formatUnits(used)}
      description={
        included
          ? `${Math.round((used / included) * 100)}% of your plan${projectedPercent === null ? '' : `, about ${projectedPercent}% by ${formatBillingDate(windowEnd)} at this rate`}`
          : 'Your plan has no session limit'
      }
      subtitle={`Resets ${formatBillingDate(windowEnd)}`}
    >
      {included && (
        <div
          className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={included}
          aria-valuenow={Math.min(used, included)}
          aria-label="Sessions used this period"
        >
          <div className="bg-primary h-full rounded-full" style={{ width: `${percent}%` }} />
        </div>
      )}
    </MetricTile>
  );
};

type PlannedUpgradeNoticeProps = {
  allowance: BillingAllowance;
  canManage: boolean;
  isKeeping: boolean;
  onKeep: () => void;
};

type HeldNoticeProps = { allowance: BillingAllowance };

/**
 * Shown after "Keep my plan": the account is held to its plan's sessions. Says so while there is
 * room, and says further sessions are ignored once they are used up. Choosing another plan ends it.
 */
const HeldNotice = ({ allowance }: HeldNoticeProps) => {
  const { plan, included, capped, windowEnd } = allowance;
  const name = BILLING_PLAN_CATALOGUE[plan].name;

  return (
    <div
      className={cn(
        'mb-4 rounded-md border p-3',
        capped ? 'border-danger-line bg-danger-bg text-danger' : 'border-border bg-muted',
      )}
    >
      <p className="supporting">
        {capped
          ? `You have used all ${formatUnits(included ?? 0)} sessions on ${name}, so further sessions are ignored until ${formatBillingDate(windowEnd)}. This applies to all of Pertento. Choose a larger plan to lift it now.`
          : `You are staying on ${name}. Sessions over its ${formatUnits(included ?? 0)} limit are ignored until ${formatBillingDate(windowEnd)}. Choose a larger plan to avoid that.`}
      </p>
    </div>
  );
};

/**
 * The announced move up, with the way to decline it. Declining asks first: it holds the
 * account to its plan's sessions for this period and the next, and says what that means.
 * Owners and Admins get the button; everyone else sees the notice, since it is their plan too.
 */
const PlannedUpgradeNotice = ({ allowance, canManage, isKeeping, onKeep }: PlannedUpgradeNoticeProps) => {
  const [confirming, setConfirming] = useState(false);
  const { plannedUpgrade, plan, included } = allowance;
  if (!plannedUpgrade) return null;

  const current = BILLING_PLAN_CATALOGUE[plan].name;
  const next = BILLING_PLAN_CATALOGUE[plannedUpgrade.plan].name;

  const handleConfirm = () => {
    onKeep();
    setConfirming(false);
  };

  return (
    <div className="border-border bg-muted mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
      <p className="supporting">
        You have gone over your {current} sessions, so your plan moves up to {next} on{' '}
        {formatBillingDate(plannedUpgrade.appliesAt)}.
      </p>
      {canManage && (
        <Button variant="outline" size="sm" disabled={isKeeping} onClick={() => setConfirming(true)}>
          {isKeeping ? 'Saving...' : 'Keep my plan'}
        </Button>
      )}
      <KeepPlanDialog
        open={confirming}
        plan={plan}
        nextPlan={plannedUpgrade.plan}
        appliesAt={plannedUpgrade.appliesAt}
        included={included}
        windowEnd={allowance.windowEnd}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};
